import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, X } from 'lucide-react';
import { cx } from '@/lib/cx';
import { Button, IconButton } from './Button';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** 背景クリックで閉じる（入力途中の誤操作防止のため既定 false） */
  closeOnBackdrop?: boolean;
  /** ESC キーで閉じる（既定 true） */
  closeOnEsc?: boolean;
}

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** モーダルダイアログ。body 直下へポータル描画・フォーカストラップ・スクロールロック付き */
export function Modal({ open, onClose, title, children, footer, size = 'md', closeOnBackdrop = false, closeOnEsc = true }: ModalProps) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const prevFocus = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const dlg = dialogRef.current;
    const first = dlg?.querySelector<HTMLElement>('[data-autofocus]') ?? dlg?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? dlg)?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && closeOnEsc) {
        e.stopPropagation();
        onCloseRef.current();
      }
      if (e.key === 'Tab' && dlg) {
        const items = Array.from(dlg.querySelectorAll<HTMLElement>(FOCUSABLE));
        if (items.length === 0) return;
        const firstEl = items[0]!;
        const lastEl = items[items.length - 1]!;
        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      prevFocus?.focus?.();
    };
  }, [open, closeOnEsc]);

  if (!open) return null;
  return createPortal(
    <div className="bs-modal-backdrop" onMouseDown={(e) => closeOnBackdrop && e.target === e.currentTarget && onClose()}>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} className={cx('bs-modal', `bs-modal--${size}`)}>
        <header className="bs-modal__header">
          <h2 id={titleId} className="bs-modal__title">
            {title}
          </h2>
          <IconButton label="閉じる" icon={<X size={18} />} onClick={onClose} />
        </header>
        <div className="bs-modal__body">{children}</div>
        {footer && <footer className="bs-modal__footer">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  title: ReactNode;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** danger: 削除など取り消せない操作 */
  tone?: 'primary' | 'danger';
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/** 確認ダイアログ。命令的に使う場合は useConfirm() を推奨 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'OK',
  cancelLabel = 'キャンセル',
  tone = 'primary',
  loading,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      size="sm"
      title={
        <span className="bs-confirm__title">
          {tone === 'danger' && <AlertTriangle size={20} className="bs-confirm__icon" aria-hidden />}
          {title}
        </span>
      }
      footer={
        <>
          <Button onClick={onCancel} disabled={loading} data-autofocus={tone === 'danger' ? true : undefined}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            className={tone === 'danger' ? 'is-solid' : undefined}
            onClick={onConfirm}
            loading={loading} data-autofocus={tone === 'danger' ? undefined : true}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {message && <div className="bs-confirm__message">{message}</div>}
    </Modal>
  );
}
